// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { MapContext } from '@/contexts/map-context';
import { Loader } from '@googlemaps/js-api-loader';
import { Box } from '@mui/material';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

const loader = new Loader({ apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY });

// This zoom level will roughly show the entire United States.
const DEFAULT_MAP_ZOOM = 3.9;

// This will hide the keyboard shortcuts for the map.
const DEFAULT_MAP_KEYBOARD_SHORTCUTS = false;

// These coordinates roughly represent the geographic center of the United
// States.
const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = { lat: 38.8, lng: -98.6 };

interface Props {
  /**
   * The center of the map.
   */
  center?: { latitude: number; longitude: number } | null;
}

/**
 * A map that displays a Google Maps instance.
 */
export function Map(props: PropsWithChildren<Props>) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    loader.importLibrary('maps').then(({ Map }) => {
      // The latitude and longitude properties of the center prop are not
      // optional, but the center prop itself is optional and can be null. It
      // wouldn't make sense to use the latitude or longitude of the center prop
      // separately, so it might be slightly more intuitive to use the prop
      // directly, e.g.
      //
      // const center = props.center ?? DEFAULT_MAP_CENTER;
      //
      // However, this creates an effect dependency on the entire center prop,
      // which is an object and thus compared by reference in the effect's
      // dependency array. Passing the responsibility of maintaining referential
      // equality to the caller should be avoided, so the scalar values are used
      // instead to have React perform a value comparison instead of a reference
      // comparison. This achieves the desired result of not running this effect
      // (which recreates the map) on each render, but instead only when the
      // underlying latitude or longitude values change. Given the type of the
      // center prop, latitude and longitude are guaranteed to be provided
      // together or not at all so it is safe to use them directly.
      const center: google.maps.LatLngLiteral = {
        lat: props.center?.latitude ?? DEFAULT_MAP_CENTER.lat,
        lng: props.center?.longitude ?? DEFAULT_MAP_CENTER.lng,
      };

      // Ensure that the map instance is only created once, and subsequent prop
      // changes only update the map instead of creating a new one.
      setMap((currentMap) => {
        if (mapContainerRef.current === null) {
          return currentMap;
        }

        if (currentMap === null) {
          const map = new Map(mapContainerRef.current, {
            center,
            disableDefaultUI: true,
            zoom: DEFAULT_MAP_ZOOM,
            keyboardShortcuts: DEFAULT_MAP_KEYBOARD_SHORTCUTS,
            mapId: import.meta.env.VITE_GOOGLE_MAPS_LOCATION_MAP_ID,
          });

          return map;
        }

        const currentCenter = currentMap.getCenter();
        const shouldUpdateCenter =
          currentCenter === undefined ||
          currentCenter.lat() !== center.lat ||
          currentCenter.lng() !== center.lng;

        if (shouldUpdateCenter) {
          currentMap.setCenter(center);
        }

        return currentMap;
      });
    });
  }, [props.center?.latitude, props.center?.longitude]);

  return (
    <Box ref={mapContainerRef} width="100%" height="100%">
      <MapContext.Provider value={{ map }}>
        {props.children}
      </MapContext.Provider>
    </Box>
  );
}
